import { useCallback, useState } from 'react';
import { Button, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { FilterFunctionaries } from './FilterFunctionaries';
import { useFunctionariesList } from './useFunctionariesList';
import { FunctionariesList } from './FunctionariesList';
import {
	ADD_FUNCTIONARY,
	EDIT_FUNCTIONARY,
	SELECT_MODULE,
} from '../../../shared/Routes';
import { useBatchId } from '../../../hooks/useBatchId';
import {
	FunctionaryInfo,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useRedirectToLogin } from '../../../hooks/useRedirectToLogin';
import { useAddOrEditFunctionaryPage } from '../AddOrEditFunctionaryPage/useAddOrEditFunctionaryPage';
import { UndoOutlined } from '@ant-design/icons';
import Icon from '../../../components/ui-components/Icon';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import LeftArrowBlack from '../../../assets/images/arrow-left.svg';
import FilterIcon from '../../../assets/images/filter-icon.svg';

const { Text } = Typography;

export const FunctionariesListContainer = () => {
	const {
		functionaryList,
		onPageChange,
		filter,
		onFilterChange,
		paging,
		navigateWithFunctionarySrcmId,
		isFilterTextApplied,
		setIsFilterTextApplied,
	} = useFunctionariesList();
	const { t } = useTranslation();
	const { navigateWithBatchId } = useBatchId();
	const { role } = useRedirectToLogin();
	const [isFilterPopup, setIsFilterPopup] = useState<boolean>(false);
	const [resetFilterValues, setResetFilterValues] = useState<boolean>(false);
	const displayFunctionariesLink = role === UserRole.ADMIN;
	const isApprover = role === UserRole.APPROVER;

	const { addFunctionaryStatus } = useAddOrEditFunctionaryPage();

	const handleEditProposerClick = useCallback(
		(functionary: FunctionaryInfo) => {
			navigateWithFunctionarySrcmId(EDIT_FUNCTIONARY, functionary.srcmId);
		},
		[navigateWithFunctionarySrcmId]
	);

	const handleBackClick = useCallback(() => {
		navigateWithBatchId(SELECT_MODULE);
	}, [navigateWithBatchId]);

	const handleFilterPopup = useCallback(() => {
		setIsFilterPopup(true);
	}, []);

	const onClose = useCallback(() => {
		setIsFilterPopup(false);
	}, []);

	const resetFunctionaryFilters = useCallback(() => {
		onFilterChange({ status: ['FUNCTIONARY_ASSIGNED'] });
		setResetFilterValues(true);
		setIsFilterTextApplied(false);
	}, [onFilterChange, setIsFilterTextApplied]);

	const handleAddProposerClick = useCallback(() => {
		addFunctionaryStatus();
		resetFunctionaryFilters();
		navigateWithBatchId(ADD_FUNCTIONARY);
	}, [navigateWithBatchId, resetFunctionaryFilters, addFunctionaryStatus]);

	return (
		<div className="flex flex-col min-w-[350px] max-w-[480px] sm:min-w-0 sm:max-w-none px-[16px] py-[8px]">
			<Text className="font-sans font-bold text-xl text-secondary-black mb-4">
				{isApprover ? t('listOfProposers') : t('listOfFunctionaries')}
			</Text>

			<div className="flex justify-between">
				<div className="sm:mt-0 md:mt-[16px] flex gap-4">
					{/* Back Button - Web */}
					<Button
						type="primary"
						onClick={handleBackClick}
						className="hidden md:block font-bold text-sm"
					>
						<span className="flex gap-2">
							<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
							{t('back')}
						</span>
					</Button>

					{/* Back Button - Mobile */}
					<div className="block sm:hidden">
						<a
							onClick={handleBackClick}
							className="relative top-[8px] md:top-[3px] mr-2 font-bold text-sm"
						>
							<img src={LeftArrowBlack} width={'w-4'} height={'h-4'} />
						</a>
					</div>
					{/* Filter Button - web */}
					<Button
						className={`hidden sm:block text-primary-blue font-sans font-bold text-sm border border-solid ${
							isFilterTextApplied ? 'border-primary-blue' : 'border-[#B7B9BB]'
						}`}
						variant="outlined"
						onClick={handleFilterPopup}
					>
						{isFilterTextApplied ? t('filterApplied') : t('filter')}
					</Button>

					{/* Reset Filter Button - Web */}
					{isFilterTextApplied ? (
						<Button
							className="hidden sm:block h-9 text-red-500 font-sans font-bold"
							type="link"
							onClick={resetFunctionaryFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
					) : (
						''
					)}
				</div>
				<div className="sm:mt-0 md:mt-[16px] flex sm:justify-start md:justify-end">
					{/* Filter Button - mobile */}
					<Button
						className="block sm:hidden"
						variant="outlined"
						onClick={handleFilterPopup}
					>
						<img src={FilterIcon} alt="Filter Icon" className="w-5 h-5" />
					</Button>
					{/* Add Proposer/Add Functionary - Web */}
					<Button
						type="link"
						onClick={handleAddProposerClick}
						className={`hidden md:block text-primary-blue font-sans font-bold text-sm border border-solid mt-0.5 border-primary-blue`}
						variant="outlined"
					>
						{displayFunctionariesLink ? t('addFunctionary') : t('addProposer')}
					</Button>
				</div>
			</div>
			{/* Add Proposer/Add Functionary - Mobile */}
			<Button
				onClick={handleAddProposerClick}
				className="w-full block sm:hidden text-primary-blue font-sans font-bold text-sm border border-solid border-primary-blue mt-[16px]"
			>
				{displayFunctionariesLink ? t('addFunctionary') : t('addProposer')}
			</Button>
			<div className="mb-[16px]">
				<FilterFunctionaries
					open={isFilterPopup}
					value={filter}
					onClose={onClose}
					onFilterChange={onFilterChange}
					resetFilterValues={resetFilterValues}
					setResetFilterValues={setResetFilterValues}
				/>
			</div>
			<FunctionariesList
				data={functionaryList}
				onPageChange={onPageChange}
				onEditClick={handleEditProposerClick}
				pageConfig={paging}
				navigateWithFunctionarySrcmId={navigateWithFunctionarySrcmId}
				onFilterChange={onFilterChange}
			/>
		</div>
	);
};
